/**
 * Composant Alert
 * Messages système avec variantes visuelles (info, success, warning, danger)
 * Accessibilité: role="alert" pour les lecteurs d'écran
 */
import { ReactNode } from 'react'

type AlertVariant = 'info' | 'success' | 'warning' | 'danger'

interface AlertProps {
  variant: AlertVariant
  title?: string
  children: ReactNode
  dismissible?: boolean
  onDismiss?: () => void
  icon?: ReactNode
  className?: string
}

// Styles par variante (CDC section 4.2)
const variantStyles: Record<AlertVariant, {
  container: string
  border: string
  icon: string
  title: string
  text: string
}> = {
  info: {
    container: 'bg-blue-50',
    border: 'border-l-4 border-blue-500',
    icon: 'text-blue-500',
    title: 'text-blue-800',
    text: 'text-blue-700',
  },
  success: {
    container: 'bg-green-50',
    border: 'border-l-4 border-green-500',
    icon: 'text-green-500',
    title: 'text-green-800',
    text: 'text-green-700',
  },
  warning: {
    container: 'bg-amber-50',
    border: 'border-l-4 border-amber-500',
    icon: 'text-amber-500',
    title: 'text-amber-800',
    text: 'text-amber-700',
  },
  danger: {
    container: 'bg-red-50',
    border: 'border-l-4 border-red-500',
    icon: 'text-red-500',
    title: 'text-red-800',
    text: 'text-red-700',
  },
}

// Icônes par défaut par variante
const defaultIcons: Record<AlertVariant, ReactNode> = {
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  success: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  danger: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
}

export default function Alert({
  variant,
  title,
  children,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
}: AlertProps) {
  const styles = variantStyles[variant]
  const displayIcon = icon ?? defaultIcons[variant]

  return (
    <div
      role="alert"
      className={`${styles.container} ${styles.border} rounded-r-lg p-4 ${className}`}
    >
      <div className="flex">
        {/* Icône */}
        <div className={`flex-shrink-0 ${styles.icon}`}>
          {displayIcon}
        </div>

        {/* Contenu */}
        <div className="ml-3 flex-1">
          {title && (
            <h4 className={`text-sm font-medium ${styles.title}`}>
              {title}
            </h4>
          )}
          <div className={`text-sm ${styles.text} ${title ? 'mt-1' : ''}`}>
            {children}
          </div>
        </div>

        {/* Bouton de fermeture */}
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className={`inline-flex rounded-md p-1.5 ${styles.icon} hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-offset-2`}
            >
              <span className="sr-only">Fermer</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
